import { isSubstringInArrayStrict } from 'helper/formatArray';
import { toMinorUnits } from 'helper/money';

// Missing or non-numeric values count as 0. The API can return amounts as
// strings (the freight rate is one), and `number + "25.5"` concatenates.
const num = (value) => Number(value) || 0;

/**
 * Checkout price breakdown. Pure: same inputs, same totals.
 *
 * - Nigeria: delivery fee, service charge and minimum order value come from
 *   the area's market, else the first cart item's market.
 * - Elsewhere: delivery is the country's per-kg rate times the cart weight;
 *   service charge and minimum order value come from the first item's market.
 * - Tax: a state rate matching the delivery address wins over the country's
 *   `country_tax`. VAT is the country's `vat` percentage of the subtotal.
 * - A voucher's balance (value - used_value) comes off the gross total. If it
 *   covers the gross, the total is 0 and the deduction is the whole gross.
 */
export function computeCheckoutTotals({
  cartItems = [],
  country,
  areaMarket,
  taxes = [],
  deliveryState,
  voucher,
  doorDeliveryFee = 0,
}) {
  const items = (cartItems || []).map((x) => ({
    quantity: num(x?.quantity),
    price: num(x?.priceByMarket_id?.price),
    kg: num(x?.priceByMarket_id?.kg),
    market: x?.priceByMarket_id?.market_id,
  }));

  const subtotal = items.reduce((acc, x) => acc + x.quantity * x.price, 0);
  const totalKg = items.reduce((acc, x) => acc + x.kg * x.quantity, 0);

  const isNigeria = country?.name?.toLowerCase() === 'nigeria';
  const market = isNigeria ? areaMarket || items[0]?.market : items[0]?.market;
  const deliveryFee = isNigeria
    ? num(market?.delivery_fee)
    : items.reduce(
        (acc, x) =>
          acc + num(country?.delivery_fee_by_kg) * (x.kg * x.quantity),
        0
      );

  const stateTax = deliveryState
    ? (taxes || []).find((x) =>
        isSubstringInArrayStrict(x?.state, [deliveryState])
      )
    : undefined;
  const taxRate = num(
    stateTax?.percentage !== undefined
      ? stateTax.percentage
      : country?.country_tax
  );

  const serviceCharge = num(market?.service_charge);
  const vat = (num(country?.vat) * subtotal) / 100;
  const tax = (taxRate * subtotal) / 100;
  const otherCharges = num(country?.other_charges);
  const doorFee = num(doorDeliveryFee);

  const gross =
    deliveryFee + doorFee + subtotal + serviceCharge + vat + tax + otherCharges;

  const voucherBalance = voucher
    ? Math.max(0, num(voucher.value) - num(voucher.used_value))
    : 0;
  // Compare whole cents: a balance equal to the gross covers it, and float
  // noise (74.00000000000001) must not leave a 0.00 card payment to make.
  const voucherCoversAll =
    !!voucher && toMinorUnits(voucherBalance) >= toMinorUnits(gross);
  const voucherDeduction = !voucher
    ? 0
    : voucherCoversAll
    ? gross
    : voucherBalance;

  return {
    subtotal,
    totalKg,
    market,
    deliveryFee,
    doorDeliveryFee: doorFee,
    serviceCharge,
    vat,
    taxRate,
    tax,
    otherCharges,
    minimumOrderValue: num(market?.minimum_order_value),
    gross,
    voucherBalance,
    voucherCoversAll,
    voucherDeduction,
    total: gross - voucherDeduction,
  };
}
